import { TrackingService } from '../services/TrackingService'
import { ElementClicked } from '../events/ElementClicked'
import type { ActionOutcome, CustomDimensions, ElementType } from '../types'

export type ElementClickedConfig = {
  clickType: ActionOutcome
  elementType: ElementType
  location: string
  itemPosition?: string
  itemTitle: string
  itemName?: string
  customDimensions?: CustomDimensions[]
}

/**
 * Hook for tracking element click events
 *
 * @returns A function that tracks element click events
 *
 * @example
 * ```typescript
 * const trackElementClicked = useElementClicked()
 *
 * trackElementClicked({
 *   clickType: ActionOutcome.SUCCESS,
 *   elementType: ElementType.BUTTON,
 *   location: 'header',
 *   itemPosition: '1',
 *   itemTitle: 'Sign Up',
 *   customDimensions: [
 *     {
 *       key: 'customDimensionKey',
 *       value: 'customDimensionValue',
 *     },
 *   ],
 * })
 * ```
 */
const useElementClicked =
  () =>
  (config: ElementClickedConfig): void => {
    const elementClickedEvent: ElementClicked = new ElementClicked({
      actionOutcome: config.clickType,
      webElement: {
        elementType: config.elementType,
        location: config.location,
        name: config.itemName || config.itemTitle,
        position: config.itemPosition,
        text: config.itemTitle,
      },
      customDimensions: config.customDimensions || [],
    })

    TrackingService.track(elementClickedEvent)
  }

export default useElementClicked
